/**
 * HTTP client module using Axios.
 * Provides a configured axios instance with interceptors for error handling.
 *
 * Features:
 * - Automatic base URL configuration
 * - Request/response interceptors
 * - Error transformation
 * - Request timeout handling
 * - Auth via HttpOnly cookie on management API domain (auto-sent by browser)
 * - Automatic error capture to error store
 *
 * @module services/httpClient
 */

import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/config';
import type { ApiError } from '@/types';
import { useErrorStore } from '@/stores/useErrorStore';
import { clearAuthData } from './authService';

/**
 * Check if user is authenticated by checking if the management API cookie exists.
 * Makes a lightweight request to the management API auth check endpoint.
 */
export async function checkAuthenticated(): Promise<boolean> {
  try {
    const response = await fetch(`${config.apiBaseUrl}/auth/check`, {
      credentials: 'include', // Send HttpOnly cookie
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Custom error class for API errors
 */
export class ApiException extends Error {
  public readonly status: number;
  public readonly detail?: string;
  public readonly traceId?: string;
  public readonly requestId?: string;
  public readonly errorCode?: number;

  constructor(
    message: string,
    status: number,
    detail?: string,
    traceId?: string,
    requestId?: string,
    errorCode?: number
  ) {
    super(message);
    this.name = 'ApiException';
    this.status = status;
    this.detail = detail;
    this.traceId = traceId;
    this.requestId = requestId;
    this.errorCode = errorCode;
  }
}

/**
 * Create and configure the axios instance
 */
function createHttpClient(): AxiosInstance {
  console.log('[HttpClient] Creating HTTP client with baseURL:', config.apiBaseUrl);

  const client = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 300000, // 5 minutes
    withCredentials: true, // Send HttpOnly cookie with every request
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  client.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      const fullUrl = `${requestConfig.baseURL}${requestConfig.url}`;
      console.log(`[HttpClient] 📤 REQUEST: ${requestConfig.method?.toUpperCase()} ${fullUrl}`);
      console.log('[HttpClient]   Headers:', requestConfig.headers);
      if (requestConfig.data) {
        console.log('[HttpClient]   Body:', requestConfig.data);
      }
      return requestConfig;
    },
    (error: AxiosError) => {
      console.error('[HttpClient] ❌ REQUEST ERROR:', error.message);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      console.log(`[HttpClient] 📥 RESPONSE: ${response.status} ${response.config.url}`);
      console.log('[HttpClient]   Data:', response.data);
      return response;
    },
    (error: AxiosError<ApiError>) => {
      // Transform error for consistent handling
      const status = error.response?.status || 500;
      const apiError = error.response?.data;

      const message = apiError?.title || error.message || 'An unexpected error occurred';
      const detail = apiError?.detail || error.message;
      const traceId = apiError?.traceId;
      const requestId = apiError?.requestId;
      const errorCode = apiError?.errorCode;
      const url = error.config?.url;

      console.error(`[HttpClient] ❌ RESPONSE ERROR: ${status}`);
      console.error('[HttpClient]   Message:', message);
      console.error('[HttpClient]   Detail:', detail);

      // Redirect to login page on 401 Unauthorized (same as logout button)
      if (status === 401) {
        console.warn('[HttpClient] 401 Unauthorized - logging out and redirecting to login');
        // Clear auth data (same as logout) - fire and forget since we're redirecting
        clearAuthData().catch((err) =>
          console.error('[HttpClient] Failed to clear auth data:', err)
        );
        // Redirect to login page
        window.location.href = '/login';
        return Promise.reject(error);
      }
      console.error('[HttpClient]   TraceId:', traceId);
      console.error('[HttpClient]   RequestId:', requestId);
      console.error('[HttpClient]   ErrorCode:', errorCode);
      console.error('[HttpClient]   URL:', url);
      console.error('[HttpClient]   Full error:', error);

      // Capture error to the error store for history/debugging
      try {
        useErrorStore.getState().addError({
          message,
          detail,
          status,
          errorCode,
          traceId,
          requestId,
          url,
        });
      } catch (storeError) {
        console.warn('[HttpClient] Failed to add error to store:', storeError);
      }

      throw new ApiException(message, status, detail, traceId, requestId, errorCode);
    }
  );

  return client;
}

/**
 * Singleton HTTP client instance
 */
export const httpClient = createHttpClient();

/**
 * Update the base URL of the HTTP client.
 * Useful for runtime configuration changes.
 */
export function updateBaseUrl(newBaseUrl: string): void {
  console.log('[HttpClient] Updating baseURL to:', newBaseUrl);
  httpClient.defaults.baseURL = newBaseUrl;
}
